import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import { customAxios } from "../api/requests/customAxios";
import { isSessionExpiredError } from "./session";

/**
 * Keeps the Auth0 access token fresh on every backend request.
 *
 * Pages still pass a token they read once (Redux / component state), which goes stale when it
 * expires and makes the backend answer 401 (shown to users as a permission error).
 * These interceptors replace that header with a current token from Auth0: getAccessTokenSilently
 * returns the cached token while it's valid and uses the refresh token to renew it when it isn't.
 */

type GetToken = (options?: { ignoreCache?: boolean }) => Promise<string>;

const backendOrigin = (() => {
  try {
    return new URL(process.env.REACT_APP_BACKEND_BASE_URL ?? "").origin;
  } catch {
    return null;
  }
})();

function isBackendRequest(config: AxiosRequestConfig): boolean {
  if (!backendOrigin || !config.url) return false;
  try {
    const url = new URL(config.url, config.baseURL || window.location.origin);
    return url.origin === backendOrigin;
  } catch {
    return false;
  }
}

function setAuthHeader(config: AxiosRequestConfig, token: string) {
  config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
}

interface RetryableConfig extends AxiosRequestConfig {
  _tokenRetried?: boolean;
}

/**
 * The signed-in user's Auth0 session, set by AuthenticationMiddleware (null when signed out).
 * The interceptors below are registered once at module load and read it on every request,
 * so they are in place before any page sends its first request.
 */
interface TokenSession {
  getToken: GetToken;
  /** Called whenever a renewed token differs from the last one, so stored copies can be updated */
  onToken: (token: string) => void;
  onSessionExpired: () => void;
}

let session: TokenSession | null = null;
let lastToken: string | null = null;

export function setTokenSession(newSession: TokenSession | null) {
  session = newSession;
  if (!newSession) lastToken = null;
}

async function getSessionToken(current: TokenSession, options?: { ignoreCache?: boolean }) {
  const token = await current.getToken(options);
  if (token !== lastToken) {
    lastToken = token;
    current.onToken(token);
  }
  return token;
}

function registerTokenInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use(async (config) => {
    const current = session;
    if (!current || !isBackendRequest(config)) return config;
    try {
      setAuthHeader(config, await getSessionToken(current));
    } catch (error) {
      // The session is over: sign out and don't send a request that can only fail
      if (isSessionExpiredError(error)) {
        current.onSessionExpired();
        throw error;
      }
      // Any other failure (e.g. network): keep the header the caller passed; the request may still succeed
    }
    return config;
  });

  instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    const current = session;
    const config = error.config as RetryableConfig | undefined;
    if (!current || error.response?.status !== 401 || !config || config._tokenRetried || !isBackendRequest(config)) {
      return Promise.reject(error);
    }
    // Token was rejected: force a renewal and retry the request once
    config._tokenRetried = true;
    try {
      setAuthHeader(config, await getSessionToken(current, { ignoreCache: true }));
    } catch (tokenError) {
      if (isSessionExpiredError(tokenError)) current.onSessionExpired();
      return Promise.reject(error);
    }
    return instance.request(config);
  });
}

[axios, customAxios].forEach(registerTokenInterceptors);
